import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getRfq } from "../lib/api";
import Timeline from "../components/Timeline";
import { RFQ_DETAIL } from "../constants/testIds";
import { Loader2, MapPin, Package, Truck, Paperclip, MessageSquare } from "lucide-react";
import { Badge } from "../components/ui/badge";

export default function RFQDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getRfq(id)
      .then((d) => mounted && setData(d))
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Failed to load RFQ"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(60,5%,98%)]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(60,5%,98%)] p-6">
        <div className="max-w-md text-center">
          <div className="text-2xl font-light">RFQ not found</div>
          <p className="mt-2 text-sm text-neutral-500">{error}</p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm uppercase tracking-[0.2em] font-semibold text-[hsl(217,90%,25%)]"
          >
            Return home
          </Link>
        </div>
      </div>
    );
  }

  const { rfq, buyer, workspace } = data;

  return (
    <div
      data-testid={RFQ_DETAIL.container}
      className="min-h-screen bg-[hsl(60,5%,98%)]"
    >
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
            Workspace · {workspace?.name || "—"}
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        {/* Title */}
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-neutral-200 pb-8">
          <div className="min-w-0">
            <div
              className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]"
              data-testid={RFQ_DETAIL.number}
            >
              {rfq.number}
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-light tracking-tight break-words">
              {rfq.product}
            </h1>
            <div className="mt-3 text-sm text-neutral-500">
              Submitted {new Date(rfq.created_at).toLocaleString()}
            </div>
          </div>
          <Badge
            data-testid={RFQ_DETAIL.status}
            className="rounded-sm bg-[hsl(217,90%,25%)] hover:bg-[hsl(217,90%,25%)] text-white uppercase tracking-[0.18em] text-[10px] font-semibold px-3 py-1.5"
          >
            {rfq.status}
          </Badge>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-10">
          {/* LEFT — Details */}
          <div className="lg:col-span-5 space-y-8">
            <section>
              <div className="text-xs uppercase tracking-[0.24em] font-semibold text-neutral-500">
                Request details
              </div>
              <div className="mt-4 space-y-4">
                <DetailRow icon={Package} label="Quantity" value={rfq.quantity} />
                <DetailRow
                  icon={MapPin}
                  label="Destination"
                  value={
                    rfq.destination_country +
                    (rfq.destination_port ? ` · ${rfq.destination_port}` : "")
                  }
                />
                <DetailRow icon={Truck} label="Channel" value={(rfq.channel || "web").toUpperCase()} />
                {rfq.attachments?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-2">
                      Attachments
                    </div>
                    <ul className="space-y-2">
                      {rfq.attachments.map((a) => (
                        <li
                          key={a.file_id}
                          className="flex items-center gap-3 p-3 border border-neutral-200 bg-white rounded-sm text-sm"
                        >
                          <Paperclip className="w-4 h-4 text-neutral-400" />
                          <span className="truncate">{a.filename}</span>
                          <span className="ml-auto text-xs text-neutral-400">
                            {(a.size / 1024).toFixed(0)} KB
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {rfq.specification_note && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-2">
                      Notes
                    </div>
                    <div className="p-4 border border-neutral-200 bg-white text-sm rounded-sm leading-relaxed">
                      {rfq.specification_note}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {buyer && (buyer.name || buyer.email || buyer.company) && (
              <section>
                <div className="text-xs uppercase tracking-[0.24em] font-semibold text-neutral-500">
                  Contact
                </div>
                <div className="mt-4 space-y-2 text-sm text-neutral-700">
                  {buyer.name && <div>{buyer.name}</div>}
                  {buyer.company && <div className="text-neutral-500">{buyer.company}</div>}
                  {buyer.email && (
                    <div className="text-[hsl(217,90%,25%)]">{buyer.email}</div>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* RIGHT — Timeline */}
          <div className="lg:col-span-7">
            <div className="border border-neutral-200 bg-white p-8 lg:p-10 rounded-sm">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-xs uppercase tracking-[0.24em] font-semibold text-neutral-500">
                    Pipeline
                  </div>
                  <div className="mt-2 text-lg font-medium">Your RFQ status</div>
                </div>
                <MessageSquare className="w-5 h-5 text-neutral-400" />
              </div>
              <Timeline stages={rfq.timeline} testId={RFQ_DETAIL.timeline} />
              <div className="mt-8 p-4 bg-[hsl(60,5%,96%)] rounded-sm border border-neutral-200 text-sm text-neutral-600 leading-relaxed">
                We are matching your request with verified Turkish manufacturers. You&apos;ll
                receive quotations here as soon as suppliers respond.
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 rounded-sm bg-[hsl(217,90%,25%)]/8 grid place-items-center shrink-0">
        <Icon className="w-4 h-4 text-[hsl(217,90%,25%)]" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
          {label}
        </div>
        <div className="mt-1 text-base text-neutral-900 break-words">{value}</div>
      </div>
    </div>
  );
}
